import { users } from "@/api/users/useUsersQuery";
import { customFunctions } from "@/config/customFunction";
import { useModal } from "@/features/modal/ModalStackManager";
import { FileImportForm } from "@/features/ui/form/file";
import { useInterportDownloadQuery } from "@/api/interport/useInterportGetQuery";
import { useLoader } from "@/hooks/useLoader";
import { useMenuStore } from "@/store/menuStore";
import { useUserStore } from "@/store/userStore";
import { extractMenuCodeAndLabel } from "@/utils/extractMenuName";
import { Flex, Menu, ScrollArea, Text } from "@mantine/core";
import { useResizeObserver } from "@mantine/hooks";
import {
  AuthSignupPost201Response,
  AuthSignupPost201ResponseExternalUserTypeEnum,
} from "@sizlcorp/sizl-api-document/dist/models";
import { IconChevronDown, IconChevronRight } from "@tabler/icons-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";

type LinkType = {
  label: string;
  links?: (Link | NestedLink)[];
  link?: string;
  visible?: boolean; // 추가
  target?: string;
  code?: string;
}[];

export type Link = {
  link: string;
  label: string;
  action?: boolean;
  actionKey?: "download" | "upload" | "forward" | "reverce";
  visible?: boolean; // 추가
  target?: string;
  code?: string;
  subCode?: string;
};

export type NestedLink = {
  label: string;
  links: (Link | NestedLink)[];
  action?: boolean;
  actionKey?: "download" | "upload" | "forward" | "reverce";
  visible?: boolean; // 추가
  target?: string;
  code?: string;
};

// 메뉴 Item의 최대 높이 설정. 높이가 MAX_HEIGHT보다 크면 스크롤바를 표시하기 위함
const MAX_HEIGHT = 400; // 25rem

const RenderSubMenuItems = ({ items, label }: { items: (Link | NestedLink)[]; label?: string }) => {
  const { t } = useTranslation();
  const { openModal } = useModal();
  const { loading, setLoading, LoadingOverlay } = useLoader();

  const handleDownLoad = async () => await useInterportDownloadQuery.download(setLoading);
  const handleUpload = () => openModal(<FileImportForm />, null, "기준정보 업로드");

  if (!items || items.length === 0) return null;

  return (
    <>
      {loading && <LoadingOverlay />}
      {items
        .filter((item) => !!item.visible)
        .map((item) => {
          if ("links" in item) {
            return (
              <Menu.Item component="div" key={item.label}>
                <Flex justify="space-between" align="center">
                  <Menu shadow="md" trigger="hover" position="right" withArrow withinPortal>
                    <Menu.Target>
                      <Flex
                        justify="space-between"
                        align="center"
                        style={{ cursor: "pointer", width: "100%" }}
                      >
                        <Text>{t(item.label)}</Text>
                        <IconChevronRight size="0.9rem" stroke={1.5} />
                      </Flex>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <RenderSubMenuItems items={item.links} label={item.label} />
                    </Menu.Dropdown>
                  </Menu>
                </Flex>
              </Menu.Item>
            );
          }

          return (
            <Menu.Item
              key={item.label}
              component={RouterLink}
              to={item.action ? "#" : `${item.link}?label=${item.code}&group=${label}`}
              target={item.target}
              onClick={
                item.action
                  ? item.actionKey === "download"
                    ? handleDownLoad
                    : item.actionKey === "upload"
                      ? handleUpload
                      : undefined
                  : undefined
              }
              color={item.action ? "indigo" : ""}
            >
              {t(item.label)}
            </Menu.Item>
          );
        })}
    </>
  );
};

const renderMainMenuItems = (items: LinkType, t: (key: string) => string) => {
  return items
    .filter((item) => !!item.visible)
    .map((item) => {
      const hasLinks = item.links && item.links.length > 0;

      return (
        <Menu key={item.label} shadow="md" trigger="hover" withArrow withinPortal>
          <Menu.Target>
            {item.link ? (
              <RouterLink
                to={`${item.link}?label=${item.label}`}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <Flex align="center" style={{ cursor: "pointer" }}>
                  <Text>{t(item.label)}</Text>
                </Flex>
              </RouterLink>
            ) : (
              <Flex align="center" style={{ cursor: "pointer" }}>
                <Text>{t(item.label)}</Text>
                {hasLinks && <IconChevronDown size="0.9rem" stroke={1.5} />}
              </Flex>
            )}
          </Menu.Target>
          <Menu.Dropdown>
            <DynamicHeightScrollArea>
              <RenderSubMenuItems items={item.links ?? []} label={item.label} />
            </DynamicHeightScrollArea>
          </Menu.Dropdown>
        </Menu>
      );
    });
};

const DynamicHeightScrollArea = ({ children }: { children: React.ReactNode }) => {
  const [ref, rect] = useResizeObserver();
  const calculatedHeight = rect.height > MAX_HEIGHT ? MAX_HEIGHT : rect.height;

  return (
    <ScrollArea h={calculatedHeight}>
      <Flex direction="column" ref={ref}>
        {children || <div>No children provided</div>}
      </Flex>
    </ScrollArea>
  );
};

export const MenuTemplate = () => {
  const { t, i18n } = useTranslation();

  const { userInfo, setUserInfo, setAdminUser } = useUserStore();
  const queryClient = useQueryClient();

  const who = users.who({}); // { queryKey, queryFn }

  // 캐시 있으면 초기 데이터로 넣어서 깜빡임 최소화
  const initialData = queryClient.getQueryData<AxiosResponse<AuthSignupPost201Response>>(
    who.queryKey
  );

  const { data } = useQuery({
    ...users.who({}),
    initialData,
    staleTime: 1000 * 60 * 5, // 5분 동안 캐시 유지
    refetchInterval: 1000 * 60 * 5, // 5분마다 데이터 갱신
    onSuccess: (data) => {
      setUserInfo(data?.data as AuthSignupPost201Response);
      setAdminUser(
        Array.isArray(data?.data?.permissions?.allow)
          ? !!data?.data.permissions?.allow.includes("ADMIN")
          : false
      );
    },
  });

  const effectiveUser = data?.data ?? userInfo;

  const userType = effectiveUser?.externalUserType as AuthSignupPost201ResponseExternalUserTypeEnum;
  const allowMenus = useMemo(
    () => extractMenuCodeAndLabel(userInfo?.allowedMenus ?? []),
    [userInfo?.allowedMenus]
  );
  const { setVisibleMenus } = useMenuStore();

  useEffect(() => {
    if (userType) {
      localStorage.setItem("userType", userType as AuthSignupPost201ResponseExternalUserTypeEnum);
    } else if (!userType) {
      localStorage.removeItem("userType");
    }
  }, [userType]);

  const links: LinkType = [
    { link: "/dashboard", label: t("menu.dashboard"), visible: true, code: "dashboard" },
    {
      label: "테스트",
      visible: customFunctions.ADD_BEST_PRACTICE_TEST_MENU_SETTING,
      code: "test",
      links: [
        {
          link: "/test-standard-info-best-practice",
          label: t("menu.standard.standard"),
          visible: true,
          code: "menu.standard.standard",
        },
      ],
    },
    {
      label: t("menu.system.management"),
      visible: customFunctions.ADD_SYSTEM_MANAGEMENT_MENU_SETTING,
      code: "system",
      links: [
        { link: "/system", label: "System Access Log", visible: true, code: "System Access Log" },
        {
          link: "/system-logs",
          label: t("menu.system.log"),
          visible: true,
          code: "menu.system.log",
        },
        {
          link: "/system-parameter",
          label: t("menu.system.parameter"),
          visible: true,
          code: "menu.system.parameter",
        },
      ],
    },
    {
      label: t("menu.standard.standard"),
      visible: true,
      code: "master",
      links: [
        {
          link: "/common-code",
          label: t("menu.standard.commonCode"),
          visible: customFunctions.ADD_STANDARD_INFO_COMMON_CODE_MENU_SETTING,
          code: "master:commonCode",
        },
        {
          link: "/authority",
          label: t("menu.standard.authority"),
          visible: true,
          code: "master:authority",
        },
        {
          link: "/department",
          label: t("menu.standard.department"),
          visible: customFunctions.ADD_STANDARD_INFO_DEPART_CODE_MENU_SETTING,
          code: "master:department",
        },
        { link: "/items", label: t("menu.standard.item"), visible: true, code: "master:item" },
        {
          link: "/equipment",
          label: t("menu.standard.equipment"),
          visible: true,
          code: "master:equipment",
        },
        {
          link: "/defects",
          label: t("menu.standard.defect"),
          visible: true,
          code: "master:defect",
        },
        {
          link: "/operations",
          label: t("menu.standard.operation"),
          visible: true,
          code: "master:operation",
        },
        {
          link: "/routing",
          label: t("menu.standard.routing"),
          visible: true,
          code: "master:routing",
        },
        { link: "/users", label: t("menu.standard.user"), visible: true, code: "master:user" },
        {
          link: "/userLogs",
          label: t("menu.standard.userLogs"),
          visible: customFunctions.ADD_USER_ACCESS_STATUS,
          code: "master:userLogs",
        },
        { link: "/site", label: t("menu.standard.site"), visible: true, code: "master:site" },
        {
          link: "/location",
          label: t("menu.standard.location"),
          visible: true,
          code: "master:location",
        },
        {
          link: "/routing-outsource",
          label: t("menu.standard.outsource"),
          visible: true,
          code: "master:routingOutsource",
        },
        {
          link: "/ALCmodel",
          label: t("menu.standard.ALCmodel"),
          visible: customFunctions.ADD_STANDARD_INFO_ALC_MODEL_MENU_SETTING,
          code: "master:ALCmodel",
        },
        { link: "/mold", label: t("menu.standard.mold"), visible: true, code: "master:mold" },
        {
          link: "/moldStock",
          label: t("menu.standard.moldStock"),
          visible: true,
          code: "master:moldStock",
        },
        {
          link: "/abstract-item",
          label: t("menu.standard.abstractItem"),
          visible: true,
          code: "master:itemGroup",
        },
        {
          link: "/item-category",
          label: t("menu.standard.category"),
          visible: true,
          code: "master:itemCategory",
        },
        {
          link: "/partner",
          label: t("menu.standard.partner"),
          visible: customFunctions.ADD_STANDARD_INFO_PARTNER_MENU_SETTING,
          code: "master:partner",
        },
        {
          link: "/downtimeReasons",
          label: t("menu.standard.downtimeReason"),
          visible: true,
          code: "master:downtimeReason",
        },
        {
          link: "/bom",
          label: t("menu.standard.bom"),
          visible: customFunctions.ADD_STANDARD_INFO_BOM_MENU_SETTING,
          code: "master:bom",
        },
        {
          link: "/bomSearch",
          label: t("menu.standard.bomSearch"),
          visible: customFunctions.ADD_STANDARD_INFO_BOM_SEARCH_MENU_SETTING,
          code: "master:bomSearch",
        },
        {
          link: "/bomList",
          label: t("menu.standard.bomList"),
          visible: customFunctions.ADD_STANDARD_INFO_BOM_LIST_MENU_SETTING,
          code: "master:bomList",
        },
        {
          link: "/document-file",
          label: t("menu.standard.file"),
          visible: true,
          code: "master:document",
        },
        {
          link: "/peripheral",
          label: t("menu.standard.peripheral"),
          visible: customFunctions.ADD_STANDARD_INFO_PERIPHERAL_MENU_SETTING,
          code: "master:peripheral",
        },
        {
          link: "/machine",
          label: t("menu.standard.machine"),
          visible: customFunctions.ADD_STANDARD_INFO_MACHINE_MENU_SETTING,
          code: "master:machine",
        },
        {
          link: "/grade",
          label: t("menu.standard.grade"),
          visible: customFunctions.ADD_STANDARD_INFO_GRADE_MENU_SETTING,
          code: "master:grade",
        },
        {
          link: "/vehicles",
          label: t("menu.standard.vehicle"),
          code: "master:vehicle",
          visible: true,
        },
        {
          link: "/menuList",
          label: t("menu.standard.menuList"),
          code: "master:menuList",
          visible: true,
        },
        {
          link: "/#",
          label: t("menu.standard.download"),
          action: true,
          actionKey: "download",
          visible: true,
          code: "master:download",
        },
        {
          link: "/#",
          label: t("menu.standard.upload"),
          action: true,
          actionKey: "upload",
          visible: true,
          code: "master:upload",
        },
      ],
    },
    {
      label: t("menu.production.production"),
      visible: true,
      code: "mes",
      links: [
        {
          link: "/work-by-equipment",
          label: t("menu.production.equipment"),
          visible: true,
          code: "mes:worksByEquipment",
        },
        {
          link: "/work-by-production-plan",
          label: t("menu.production.productionPlan"),
          visible: true,
          code: "mes:productionPlan",
        },
        {
          link: "/plangantchart",
          label: t("menu.production.productionPlanGantt"),
          visible: false,
          code: "mes:planGantChart",
        },
        {
          link: "/work-by-row",
          label: t("menu.production.work"),
          visible: true,
          code: "mes:workOrder",
        },
        {
          link: "/operation-outsource-work-by-row",
          label: t("menu.production.outsource"),
          visible: customFunctions.ADD_SHOW_OPERATION_OUTSOURCING_WORK,
          code: "mes:outsourceWorkOrder",
        },
        {
          link: "/work-log",
          label: t("menu.production.workLog"),
          visible: true,
          code: "mes:workLog",
        },
        {
          link: "/productionperformance",
          label: t("menu.production.performance"),
          visible: customFunctions.ADD_PERFORMANCE_MENU_SETTING,
          code: "mes:performance",
        },
        {
          link: "/productionperformanceOutsourcing",
          label: t("menu.production.outsourcePerformance"),
          visible: customFunctions.ADD_PERFORMANCE_OUTSOURCING_MENU_SETTING,
          code: "mes:outsourcePerformance",
        },
        {
          link: "/downtimeReasons-log",
          label: "정지유형별 통계",
          visible: customFunctions.ADD_DOWNTIME_REASON_LOG_MENU_SETTING,
        },
        {
          link: "/lotForward",
          label: t("menu.production.lotForward"),
          visible: true,
          code: "mes:lotTrackingForward",
        },
        {
          link: "/lotReverce",
          label: t("menu.production.lotReverce"),
          visible: true,
          code: "mes:lotTrackingBackward",
        },
        {
          link: "/outsourceOrder",
          label: "외주발주등록",
          visible: true,
          code: "mes:outsourceOrder",
        },
        {
          link: "/outsourceIncoming",
          label: "외주입고등록",
          visible: true,
          code: "mes:outsourceIncoming",
        },
      ],
    },
    {
      label: t("menu.inventory.management"),
      visible: true,
      code: "wms",
      links: [
        {
          link: "/inventory",
          label: t("menu.inventory.inventory"),
          visible: true,
          code: "wms:inventory",
        },
        {
          link: "/inventoryMain",
          label: t("woojang.woojangInventory"),
          visible: true,
          code: "wms:woojangInventory",
        },
        {
          link: "/ledgersAll",
          label: t("menu.inventory.ledger"),
          visible: true,
          code: "wms:ledger",
        },
        {
          link: "/wipInventory",
          label: t("menu.inventory.wipInventory"),
          visible: true,
          code: "wms:wipInventory",
        },
        {
          link: "/wmsLogs",
          label: t("menu.inventory.wmsLog"),
          visible: true,
          code: "wms:wmsLog",
        },
        {
          link: "/lackSafety",
          label: t("menu.inventory.lackSafety"),
          visible: customFunctions.ADD_LACK_SAFETY_MENU_SETTING,
          code: "wms:lackSafety",
        },
        {
          link: "/inventoryLife",
          label: t("menu.inventory.life"),
          visible: customFunctions.ADD_INVENTORY_LIFE_MENU_SETTING,
          code: "wms:inventoryLife",
        },
        {
          link: "/lots",
          label: t("menu.inventory.lot"),
          visible: customFunctions.ADD_LOT_MANAGEMENT,
          code: "wms:lot",
        },
        {
          link: "/receipt",
          label: "재고 수불 이력",
          visible: customFunctions.ADD_RECEIPT_HISTORY_MENU_SETTING,
          code: "wms:receipt",
        },
        {
          link: "/barcodeMerge",
          label: "재고 바코드 병합",
          visible: customFunctions.ADD_BARCODE_MERGE_MENU_SETTING,
          code: "wms:barcodeMerge",
        },
        {
          link: "/barcodeLift",
          label: "재고 바코드 해제",
          visible: customFunctions.ADD_BARCODE_LIFT_MENU_SETTING,
          code: "wms:barcodeLift",
        },
      ],
    },
    {
      label: t("menu.shipment.shipment"),
      code: "shipment",
      visible: customFunctions.ADD_STORAGE_MANAGEMENT_MENU_SETTING,
      links: [
        {
          link: "/shipment-request",
          label: t("menu.shipment.request"),
          visible: true,
          code: "shipment:request",
        },
        {
          link: "/shipment-receipt",
          label: t("menu.shipment.receipt"),
          visible: true,
          code: "shipment:receipt",
        },
        {
          link: "/shipment-status",
          label: t("menu.shipment.status"),
          visible: true,
          code: "shipment:status",
        },
      ],
    },
    {
      label: t("menu.sales.sales"),
      code: "sales",
      visible: true,
      links: [
        {
          link: "/purchaseOrder",
          label: t("menu.sales.purchaseOrder"),
          visible: !customFunctions.ADD_PURCHASE_ORDER_ITEMS_MENU_SETTING,
          code: "sales:purchaseOrder:inbound",
        },
        {
          link: "/purchaseOrderItems",
          label: t("purchaseOrderItems.purchaseOrderItems"),
          visible: !customFunctions.ADD_PURCHASE_ORDER_ITEMS_MENU_SETTING,
          code: "sales:purchaseItem:inbound",
          subCode: "orderItems.description",
        },
        {
          link: "/purchaseOrderCategory",
          label: t("purchaseOrderCategory.category"),
          visible: !customFunctions.ADD_PURCHASE_ORDER_ITEMS_MENU_SETTING,
          code: "sales:purchaseOrderCategory",
        },
        {
          link: "invoiceStatement",
          label: t("invoice.invoice"),
          visible: customFunctions.ADD_INVOICE_STATEMENT_MENU_SETTING,
          code: "sales:invoice",
          subCode: "invoice.subTitle",
        },
        {
          link: "/invoiceStatementComplete",
          label: t("invoice.invoiceComplete"),
          visible: customFunctions.ADD_INVOICE_STATEMENT_MENU_SETTING,
          code: "sales:invoiceComplete",
        },
        {
          link: "/deliveryStatus",
          label: t("menu.sales.delivery"),
          visible: true,
          code: "sales:wmsLogWithPartner",
        },
        {
          link: "/order",
          label: t("menu.sales.order"),
          visible: true,
          code: "sales:purchaseOrder:outbound",
        },
        {
          link: "/orderItems",
          label: t("orderItems.orderItems"),
          visible: true,
          code: "sales:purchaseItem:outbound",
          subCode: "orderItems.description",
        },
        {
          link: "/orderComplete",
          label: t("order.orderComplete"),
          visible: customFunctions.ADD_ORDER_COMPLETE_MENU_SETTING,
          code: "sales:purchaseItem:outboundComplete",
        },
        {
          link: "/stockPlan",
          label: t("stockPlan.stockPlan"),
          visible: true,
          code: "sales:stockPlan",
        },
        {
          link: "/transportation",
          label: t("transportation.transportation"),
          visible: true,
          code: "sales:transportation",
          subCode: "transportation.transportation",
        },
        {
          link: "/deliveryInformation",
          label: "납품정보등록",
          visible: true,
          code: "salse:deliveryInformation",
        },
      ],
    },
    {
      label: t("menu.quality.quality"),
      code: "quality",
      visible: customFunctions.ADD_QUALITY_MENU_SETTING,
      links: [
        {
          link: "/inspectSpecification",
          label: t("menu.quality.inspect"),
          visible: true,
          code: "quality:inspect",
        },
        {
          link: "/inspectionRecords",
          label: t("menu.quality.inspectRecord"),
          visible: true,
          code: "quality:inspectRecord",
        },
        {
          link: "/quality/defect-statistics",
          label: t("menu.quality.defect"),
          visible: customFunctions.ADD_DEFECT_STATISTICS_MENU_SETTING,
          code: "quality:defect",
        },
        {
          link: "/quality/work-standardList",
          label: t("menu.quality.work"),
          visible: true,
          code: "quality:workStandardList",
        },
        // { link: "/quality/change-register", label: "변경점 정보 등록" },
        {
          link: "/quality/change-list",
          label: t("menu.quality.change"),
          visible: true,
          code: "quality:changeList",
        },
        {
          link: "/quality/p-chart",
          label: t("menu.quality.pChart"),
          visible: customFunctions.ADD_QUALITY_MENU_SETTING,
          code: "quality:pChart",
        },
        {
          link: "/pareto-chart",
          label: t("menu.quality.paretoChart"),
          visible: customFunctions.ADD_QUALITY_MENU_SETTING,
          code: "quality:paretoChart",
        },
        {
          link: "/quality/pre-warehouseList",
          label: "수입검사",
          visible: customFunctions.ADD_PRE_WAREHOUSE_LIST_MENU_SETTING,
        },
        {
          link: "/inspectList",
          label: "초중종 검사LIST",
          visible: true,
          code: "quality:inspectList",
        },
        {
          link: "/quality/changeList",
          label: "변경점 정보LIST",
          visible: true,
          code: "quality:changeListVision",
        },
        {
          link: "/quality/changeRegister",
          label: "변경점 정보등록",
          visible: true,
          code: "quality:changeRegisterVision",
        },
      ],
    },
    {
      label: "KPI",
      code: "kpi",
      visible: customFunctions.ADD_KPI_MENU_SETTING,
      links: [
        {
          link: "/kpi/leadtime",
          label: t("menu.kpi.leadtime"),
          visible:
            customFunctions.ADD_KPI_MENU_SETTING && !customFunctions.ADD_BTTELECOM_KPI_MENU_SETTING,
          code: "kpi:leadTime",
        },
        {
          link: "/kpi/manhour",
          label: t("menu.kpi.manhour"),
          visible:
            customFunctions.ADD_KPI_MENU_SETTING && !customFunctions.ADD_BTTELECOM_KPI_MENU_SETTING,
          code: "kpi:manHourCost",
        },
        {
          link: "/kpi/defect",
          label: t("menu.kpi.defect"),
          visible: customFunctions.ADD_KPI_MENU_SETTING,
          code: "kpi:defectRate",
        },
        {
          link: "/kpi/leadtimeOrder",
          label: t("menu.kpi.leadtimeOrder"),
          visible:
            customFunctions.ADD_KPI_MENU_SETTING && !customFunctions.ADD_BTTELECOM_KPI_MENU_SETTING,
          code: "kpi:orderLeadTime",
        },
        {
          link: "/kpi/powerUsage",
          label: t("menu.kpi.powerUsage"),
          visible:
            customFunctions.ADD_KPI_MENU_SETTING && !customFunctions.ADD_BTTELECOM_KPI_MENU_SETTING,
          code: "kpi:electricPower",
        },
        {
          link: "/kpi/uph",
          label: t("menu.kpi.uph"),
          visible:
            customFunctions.ADD_KPI_MENU_SETTING && !customFunctions.ADD_BTTELECOM_KPI_MENU_SETTING,
          code: "kpi:uph",
        },
        {
          link: "/kpi/rateOfOperation",
          label: t("menu.kpi.rateOperation"),
          visible: customFunctions.ADD_KPI_MENU_SETTING,
          code: "kpi:capacityUtilization",
        },
        {
          link: "/dataDisplay",
          label: t("menu.kpi.dataDisplay"),
          visible: customFunctions.ADD_DATA_DISPLAY_MENU_SETTING,
          code: "kpi:dataDisplay",
        },
      ],
    },
    {
      label: t("menu.monitoring.monitoring"),
      code: "monitoring",
      visible: customFunctions.ADD_MONITORING_MENU_SETTING,
      links: [
        {
          link: "/overview",
          label: t("menu.monitoring.mes"),
          visible: true,
          code: "monitoring:mesDashboard",
        },
        {
          link: "/monitoring",
          label: t("menu.monitoring.monitoring"),
          visible: true,
          code: "monitoring:status",
        },
        {
          link: "/equipmentAnalysis",
          label: t("menu.monitoring.equipment"),
          visible: customFunctions.ADD_DASHBOARD_EQUIPMENT_ANALYSIS,
          code: "monitoring:equipmentAnalysis",
        },
        {
          link: "/pressMonitoring/factoryMonitoring",
          label: t("menu.monitoring.pms"),
          visible: customFunctions.ADD_PMS_PRESS_MONITORING,
          code: "monitoring:pmsMonitoring",
        },
      ],
    },
    {
      label: "PMS",
      code: "pms",
      visible: customFunctions.ADD_PMS_MENU_SETTING,
      links: [
        {
          link: "/pressMonitoring",
          label: t("menu.pms.pressMonitoring.pressMonitoring"),
          visible: true,
          code: "pms:pressMonitoring.pressMonitoring",
          links: [
            {
              link: "/pressMonitoring/monitoring",
              label: t("menu.pms.pressMonitoring.monitoring"),
              visible: true,
              code: "pms:pressMonitoring.monitoring",
            },
            {
              link: "/pressMonitoring/factoryMonitoring",
              label: t("menu.pms.pressMonitoring.factoryMonitoring"),
              visible: true,
              code: "pms:pressMonitoring.factoryMonitoring",
            },
            {
              link: "/pressMonitoring/leadtimeOrder",
              label: t("menu.pms.pressMonitoring.leadtimeOrder"),
              visible: true,
              code: "pms:pressMonitoring.leadtimeOrder",
            },
            {
              link: "/pressMonitoring/powerUsage",
              label: t("menu.pms.pressMonitoring.powerUsage"),
              visible: true,
              code: "pms:pressMonitoring.powerUsage",
            },
            {
              link: "/pressMonitoring/uph",
              label: t("menu.pms.pressMonitoring.uph"),
              visible: true,
              code: "pms:pressMonitoring.uph",
            },
            {
              link: "/pressMonitoring/rateOfOperation",
              label: t("menu.pms.pressMonitoring.rateOperation"),
              visible: true,
              code: "pms:pressMonitoring.rateOperation",
            },
          ],
        },
        // {
        //   link: "/monitoring",
        //   label: t("menu.pms.monitoring"),
        //   visible: true,
        //   code: "menu.pms.monitoring",
        // },
        // {
        //   link: "/pressMonitoring/factoryMonitoring",
        //   label: t("menu.pms.analysisMonitoring"),
        //   visible: true,
        //   code: "menu.pms.analysisMonitoring",
        // },
        {
          link: "/press",
          label: t("menu.pms.press.management"),
          visible: true,
          code: "pms:press.management",
          links: [
            {
              link: "/press/errorview",
              label: t("menu.pms.press.error"),
              visible: true,
              code: "pms:press.error",
            },
            {
              link: "/press/parameterview",
              label: "파라미터 보기",
              visible: true,
              code: "pms:press.parameterView",
            },
            { link: "/press/camview", label: "캠 보기", visible: true, code: "pms:press.camView" },
            {
              link: "/press/clutchandbrake",
              label: "클러치 & 브레이크",
              visible: true,
              code: "pms:press.clutchAndBrake",
            },
            {
              link: "/press/facilities",
              label: t("menu.pms.press.repair"),
              visible: true,
              code: "pms:press.repair",
            },
            {
              link: "/press/facilitiesList",
              label: t("menu.pms.press.repairList"),
              visible: true,
              code: "pms:press.repairList",
            },
            {
              link: "/press/complete/admin",
              label: t("menu.pms.press.repairComplete"),
              visible: true,
              code: "pms:press.repairComplete",
            },
            {
              link: "/press/problem",
              label: t("menu.pms.press.problem"),
              visible: true,
              code: "pms:press.problem",
            },
            {
              link: "/press/PressDailyRegister",
              label: t("menu.pms.press.dailyRegister"),
              visible: true,
              code: "pms:press.dailyRegister",
            },
            {
              link: "/press/PressDailyStatus",
              label: t("menu.pms.press.dailyStatus"),
              visible: true,
              code: "pms:press.dailyStatus",
            },
          ],
        },
        {
          link: "/analysis",
          label: t("menu.pms.analysis.analysis"),
          visible: true,
          code: "pms:analysis",
          links: [
            {
              link: "/analysis/output",
              label: t("menu.pms.analysis.output"),
              visible: true,
              code: "pms:analysis.output",
            },
            {
              link: "/analysis/ability",
              label: t("menu.pms.analysis.ability"),
              visible: true,
              code: "pms:analysis.ability",
            },
            // { link: '/analysis/totaloutput', label: '총생산량', visible: true },
            {
              link: "/analysis/error",
              label: t("menu.pms.analysis.error"),
              visible: true,
              code: "pms:analysis.error",
            },
            {
              link: "/analysis/power",
              label: t("menu.pms.analysis.power"),
              visible: true,
              code: "pms:analysis.power",
            },
            {
              link: "/analysis/idleTime",
              label: t("menu.pms.analysis.idleTime"),
              visible: true,
              code: "pms:analysis.idleTime",
            },
            {
              link: "/analysis/workTime",
              label: t("menu.pms.analysis.workTime"),
              visible: true,
              code: "pms:analysis.workTime",
            },
          ],
        },
        {
          link: "/moldManagement",
          label: t("menu.pms.mold.management"),
          links: [
            {
              link: "/mold/maintenance",
              label: t("menu.pms.mold.maintenance"),
              visible: true,
              code: "pms:mold.maintenance",
            },
            {
              link: "/mold/repairs",
              label: t("menu.pms.mold.repair"),
              visible: true,
              code: "pms:mold.repair",
            },
            {
              link: "/mold/complete/admin",
              label: t("menu.pms.mold.repairComplete"),
              visible: true,
              code: "pms:mold.repairComplete",
            },
            {
              link: "/mold/problem",
              label: t("menu.pms.mold.problem"),
              visible: true,
              code: "pms:mold.problem",
            },
            {
              link: "/mold/MoldDailyRegister",
              label: t("menu.pms.mold.dailyRegister"),
              visible: true,
              code: "pms:mold.dailyRegister",
            },
            {
              link: "/mold/MoldDailyStatus",
              label: t("menu.pms.mold.dailyStatus"),
              visible: true,
              code: "pms:mold.dailyStatus",
            },
          ],
          visible: customFunctions.ADD_EQUIPMENT_MOLD_MENU_SETTING,
        },
      ],
    },
    {
      label: t("menu.pms.monitoring"),
      visible: customFunctions.ADD_EQUIPMENT_MONITORING_MENU_SETTING,
      links: [
        {
          label: t("menu.pms.monitoring"),
          links: [
            {
              link: "/overview",
              label: t("menu.monitoring.equipment"),
              visible: true,
              code: "menu.monitoring.equipment",
            },
            {
              link: "/monitoring",
              label: t("menu.monitoring.monitoring"),
              visible: true,
              code: "menu.monitoring.monitoring",
            },
            {
              link: "/analysisMonitoring",
              label: t("menu.equipment.analysisMonitoring"),
              visible: true,
              code: "menu.equipment.analysisMonitoring",
            },
            {
              link: "/cncMonitoring",
              label: t("menu.equipment.cncMonitoring"),
              visible: customFunctions.ADD_CMS,
              code: "menu.equipment.cncMonitoring",
            },
          ],
          visible: true,
        },
        {
          label: t("menu.equipment.management.management"),
          links: [
            {
              link: "/errorView",
              label: t("menu.equipment.management.error"),
              visible: true,
              code: "menu.equipment.management.error",
            },
            {
              link: "/maintenance",
              label: t("menu.equipment.management.repair"),
              visible: true,
              code: "menu.equipment.management.repair",
            },
            {
              link: "/maintenanceList",
              label: t("menu.equipment.management.repairList"),
              visible: true,
              code: "menu.equipment.management.repairList",
            },
            {
              link: "/maintenanceComplete",
              label: t("menu.equipment.management.repairComplete"),
              visible: true,
              code: "menu.equipment.management.repairComplete",
            },
            {
              link: "/equipmentProblem",
              label: t("menu.equipment.management.problem"),
              visible: true,
              code: "menu.equipment.management.problem",
            },
            {
              link: "/dailyRegister",
              label: t("menu.equipment.management.dailyRegister"),
              visible: true,
              code: "menu.equipment.management.dailyRegister",
            },
            {
              link: "/dailyList",
              label: t("menu.equipment.management.dailyStatus"),
              visible: true,
              code: "menu.equipment.management.dailyStatus",
            },
          ],
          visible: true,
        },
        {
          label: t("menu.equipment.statistical"),
          links: [
            {
              link: "/output",
              label: t("menu.pms.analysis.output"),
              visible: true,
              code: "menu.pms.analysis.output",
            },
            {
              link: "/error",
              label: t("menu.pms.analysis.error"),
              visible: true,
              code: "menu.pms.analysis.error",
            },
            {
              link: "/power",
              label: t("menu.pms.analysis.power"),
              visible: true,
              code: "menu.pms.analysis.power",
            },
            {
              link: "/idleTime",
              label: t("menu.pms.analysis.idleTime"),
              visible: true,
              code: "menu.pms.analysis.idleTime",
            },
            {
              link: "/workTime",
              label: t("menu.pms.analysis.workTime"),
              visible: true,
              code: "menu.pms.analysis.workTime",
            },
          ],
          visible: true,
        },
      ],
    },
    {
      label: "SCM",
      code: "scm",
      visible:
        customFunctions.ADD_SCM_MENU_SETTING &&
        (userType === AuthSignupPost201ResponseExternalUserTypeEnum.SALES ||
          userType === AuthSignupPost201ResponseExternalUserTypeEnum.PURCHASE),
      links: [
        {
          label: t("menu.scm.client.sales"),
          links: [
            {
              link: "/purchaseOrder",
              label: t("menu.scm.client.purchaseOrder"),
              visible: true,
              code: "scm:purchaseOrder.client",
            },
            {
              link: "/purchaseOrderItems",
              label: t("menu.scm.client.purchaseOrderItem"),
              visible: true,
              code: "scm:purchaseOrderItem.client",
            },
            // { link: "/purchaseOrderItems-gyeongnam", label: "수주현황 (경남)", visible: true },
            // { link: "/deliveryStatus-gyeongnam", label: "납품정보등록 (경남)", visible: true },
            {
              link: "/deliveryStatus",
              label: t("menu.scm.client.delivery"),
              visible: true,
              code: "scm:delivery.client",
            },
            // { link: "/deliveryList-gyeongnam", label: "납품현황 (경남)", visible: true },
          ],
          visible: userType === "SALES" ? true : false,
        },
        {
          label: t("menu.scm.partner.sales"),
          links: [
            // { link: "/order-gyeongnam", label: "발주등록 (경남)", visible: true },
            {
              link: "/order",
              label: t("menu.scm.partner.order"),
              visible: true,
              code: "scm:purchaseOrder.partner",
            },
            {
              link: "/orderItems",
              label: t("menu.scm.partner.orderItems"),
              visible: true,
              code: "scm:purchaseOrderItem.partner",
            },
            // { link: "/deliveryStatus-supplier", label: "납품정보등록 (협력사)", visible: true },
            // { link: "/deliveryList-gyeongnam", label: "납품현황 (경남)", visible: true },
            {
              link: "/deliveryStatus",
              label: t("menu.scm.partner.delivery"),
              visible: true,
              code: "scm:delivery.partner",
            },
          ],
          visible:
            userType === AuthSignupPost201ResponseExternalUserTypeEnum.PURCHASE ? true : false,
        },
        {
          label: t("menu.scm.partner.receiving"),
          links: [
            {
              link: "/shipment-request-supplier",
              label: t("menu.scm.partner.receivingRequest"),
              visible: true,
              code: "scm:receivingRequest",
            },
            {
              link: "/warehousingRegister",
              label: t("menu.scm.partner.receivingReceipt"),
              visible: true,
              code: "scm:receivingReceipt",
            },
            {
              link: "/shipment-status-supplier",
              label: t("menu.scm.partner.receivingStatus"),
              visible: true,
              code: "scm:receivingStatus",
            },
          ],
          visible:
            userType === AuthSignupPost201ResponseExternalUserTypeEnum.PURCHASE ? true : false,
        },
      ],
    },
    {
      label: t("menu.gps.gps"),
      visible: customFunctions.ADD_GPS_SYSTEM_MENU_SETTING,
      links: [
        {
          link: "http://taekang-tracker.sizl.co.kr/",
          label: t("menu.gps.tracking"),
          visible: true,
          target: "_blank",
          code: "menu.gps.tracking",
        },
      ],
    },
    {
      label: t("menu.cms.cms"),
      code: "cms",
      visible: customFunctions.ADD_CMS,
      links: [
        {
          link: "/cms/operation",
          label: t("menu.cms.operation"),
          visible: true,
          code: "cms:operation",
        },
        {
          link: "/cms/management",
          label: t("menu.cms.management"),
          visible: true,
          code: "cms:management",
        },
        {
          label: t("menu.cms.settings.settings"),
          links: [
            {
              link: "/users",
              label: t("menu.cms.settings.usersSetting"),
              visible: true,
              code: "cms:settings.usersSetting",
            },
            {
              link: "/equipment",
              label: t("menu.cms.settings.adminSetting"),
              visible: true,
              code: "cms:settings.adminSetting",
            },
            {
              link: "/equipment",
              label: t("menu.cms.settings.licenseSetting"),
              visible: true,
              code: "cms:settings.licenseSetting",
            },
          ],
          visible: true,
        },
      ],
    },
  ];

  const [filteredLinks, setFilteredLinks] = useState<LinkType>(links);

  const updateLinks = (currentAllowMenus: { links: string[]; labels: Record<string, string> }) => {
    const tokenUserType = localStorage.getItem("userType") ?? "";
    const allowedSubLabels = [t("menu.production.outsource"), t("menu.production.slitting")];

    if (!currentAllowMenus || currentAllowMenus.links.length === 0) {
      setFilteredLinks(links);
      // Convert LinkType to NestedLink[] and ensure links property is always an array
      setVisibleMenus(
        links
          .filter((item) => !!item.visible)
          .map((item) => ({
            ...item,
            links: item.links ?? [],
            label: item.label,
          }))
      );
      return;
    }

    const makeAllVisible = (links?: (Link | NestedLink)[]): (Link | NestedLink)[] => {
      return (links || []).map((link) => {
        if ("links" in link && Array.isArray(link.links)) {
          return {
            ...link,
            visible: true,
            label: link.label,
            links: makeAllVisible(link.links), // 반드시 배열을 반환
          };
        }

        return {
          ...link,
          label: link.label,
          visible: true,
        };
      });
    };

    // 현재 visibel이 true인 것만 가져와야함 menuListForm에서 보여져야함
    const menuFilter = links.reduce<NestedLink[]>((acc, item) => {
      if (!item.visible) return acc;
      if ("links" in item) {
        acc.push({ ...item, links: item.links ?? [], label: item.label });
      } else {
        acc.push({ ...item, links: item.links ?? [], label: item.label });
      }
      return acc;
    }, []);

    setVisibleMenus(menuFilter);

    const filtered = links
      .map((link) => {
        const isLinkVisible = currentAllowMenus.links.includes(link.code ?? "");
        const linkLabel = currentAllowMenus.labels[link.code ?? ""];

        if ("links" in link && Array.isArray(link.links)) {
          const filteredSub = link.links
            .map((sub) => {
              const isSubVisible =
                isLinkVisible && currentAllowMenus.links.includes(sub.code ?? "");
              const subLabel = currentAllowMenus.labels[sub.code ?? ""];

              if ("links" in sub && Array.isArray(sub.links)) {
                const filteredInner = sub.links
                  .map((inner) => {
                    const isInnerVisible =
                      isSubVisible && currentAllowMenus.links.includes(inner.code ?? "");
                    const innerLabel = currentAllowMenus.labels[inner.code ?? ""];

                    return {
                      ...inner,
                      visible: isInnerVisible,
                      label: innerLabel, // label 주입
                    };
                  })
                  .filter((inner) => inner.visible);

                return {
                  ...sub,
                  visible: isSubVisible && filteredInner.length > 0,
                  links: filteredInner,
                  label: subLabel, // label 주입
                };
              }

              return {
                ...sub,
                visible: isSubVisible,
                label: subLabel, // label 주입
              };
            })
            .filter((sub) => sub.visible);

          return {
            ...link,
            visible: isLinkVisible && filteredSub.length > 0,
            links: filteredSub,
            label: linkLabel, // label 주입
          };
        }

        return {
          ...link,
          visible: isLinkVisible,
          label: linkLabel, // label 주입
        };
      })
      .filter((link) => link.visible);

    const finalFiltered = filtered.map((link) => {
      if (
        tokenUserType === AuthSignupPost201ResponseExternalUserTypeEnum.SALES ||
        tokenUserType === AuthSignupPost201ResponseExternalUserTypeEnum.PURCHASE
      ) {
        if (link.label !== "SCM") return { ...link, visible: false };

        const filteredSub = link.links?.map((sub) => {
          if (!("links" in sub)) return { ...sub, visible: false };

          const showSubLabel =
            tokenUserType === AuthSignupPost201ResponseExternalUserTypeEnum.PURCHASE
              ? sub.label === "영업관리(공급사)" || sub.label === "입고관리(공급사)"
              : sub.label === "영업관리(고객사)";

          const filteredInner = sub.links.filter((inner) => inner.visible);

          return {
            ...sub,
            visible: showSubLabel,
            links: showSubLabel ? filteredInner : [],
          };
        });

        return { ...link, visible: true, links: filteredSub };
      }

      if (tokenUserType === AuthSignupPost201ResponseExternalUserTypeEnum.BI_DIRECTION) {
        if (link.label === "SCM") {
          return { ...link, visible: true, links: makeAllVisible(link.links) };
        }
        return { ...link, visible: false };
      }

      if (tokenUserType === AuthSignupPost201ResponseExternalUserTypeEnum.OUTSOURCING) {
        if (link.label === t("menu.production.production")) {
          const productionLinks = link.links?.map((sub) => ({
            ...sub,
            visible: allowedSubLabels.includes(sub.label),
          }));
          return { ...link, visible: true, links: productionLinks };
        }
        return { ...link, visible: false };
      }

      return link;
    });

    setFilteredLinks(finalFiltered);
  };

  useEffect(() => {
    updateLinks(allowMenus);
  }, [effectiveUser?.externalUserType, i18n.language, t, allowMenus]);

  return (
    <Flex gap="md" align={"center"} style={{ whiteSpace: "pre-wrap" }}>
      {Array.isArray(filteredLinks) ? renderMainMenuItems(filteredLinks, t) : null}
    </Flex>
  );
};
